import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Eye, EyeOff, Save, CheckCircle2, XCircle, Loader2, CreditCard, FileText, PlugZap, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('adminToken')}`,
});

/**
 * Reusable secret input with show/hide + placeholder indicating currently-saved masked value.
 */
const SecretInput = ({ value, onChange, placeholder, currentMasked, disabled, testId }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={currentMasked || placeholder}
        disabled={disabled}
        className="bg-slate-800 border-slate-700 text-white pr-10 font-mono"
        data-testid={testId}
        autoComplete="off"
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
        aria-label="Toggle visibility"
      >
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
};

const Integrations = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ razorpay: null, zoho: null });
  const [rzpForm, setRzpForm] = useState({ key_id: '', key_secret: '', webhook_secret: '', mode: '' });
  const [zohoForm, setZohoForm] = useState({
    client_id: '', client_secret: '', refresh_token: '', organization_id: '', redirect_uri: '', data_center: '',
  });
  const [saving, setSaving] = useState({ razorpay: false, zoho: false });
  const [testing, setTesting] = useState({ razorpay: false, zoho: false });
  const [testResult, setTestResult] = useState({ razorpay: null, zoho: null });

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/integrations`, { headers: getAuthHeader() });
      setData(res.data);
      setRzpForm((f) => ({ ...f, mode: res.data.razorpay?.mode || '' }));
      setZohoForm((f) => ({
        ...f,
        organization_id: res.data.zoho?.organization_id || '',
        redirect_uri: res.data.zoho?.redirect_uri || '',
        data_center: res.data.zoho?.data_center || 'in',
      }));
    } catch (err) {
      toast.error('Failed to load integration settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // --- Razorpay ---
  const saveRazorpay = async () => {
    // Only send non-empty fields; mode toggles even if not changed
    const payload = {};
    if (rzpForm.key_id) payload.key_id = rzpForm.key_id;
    if (rzpForm.key_secret) payload.key_secret = rzpForm.key_secret;
    if (rzpForm.webhook_secret) payload.webhook_secret = rzpForm.webhook_secret;
    if (rzpForm.mode) payload.mode = rzpForm.mode;
    if (Object.keys(payload).length === 0) {
      toast.error('Enter at least one field to save');
      return;
    }
    setSaving((s) => ({ ...s, razorpay: true }));
    try {
      await axios.put(`${API}/admin/integrations/razorpay`, payload, { headers: getAuthHeader() });
      toast.success('Razorpay settings saved');
      setRzpForm({ key_id: '', key_secret: '', webhook_secret: '', mode: rzpForm.mode });
      setTestResult((r) => ({ ...r, razorpay: null }));
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save Razorpay settings');
    } finally {
      setSaving((s) => ({ ...s, razorpay: false }));
    }
  };

  const testRazorpay = async () => {
    setTesting((t) => ({ ...t, razorpay: true }));
    setTestResult((r) => ({ ...r, razorpay: null }));
    try {
      const res = await axios.post(`${API}/admin/integrations/razorpay/test`, {}, { headers: getAuthHeader() });
      setTestResult((r) => ({ ...r, razorpay: res.data }));
      if (res.data.success) toast.success(res.data.message);
      else toast.error(res.data.message);
    } catch (err) {
      setTestResult((r) => ({ ...r, razorpay: { success: false, message: err.response?.data?.detail || 'Test failed' } }));
      toast.error('Test failed');
    } finally {
      setTesting((t) => ({ ...t, razorpay: false }));
    }
  };

  // --- Zoho ---
  const saveZoho = async () => {
    const payload = {};
    ['client_id', 'client_secret', 'refresh_token', 'organization_id', 'redirect_uri', 'data_center'].forEach((k) => {
      if (zohoForm[k]) payload[k] = zohoForm[k];
    });
    if (Object.keys(payload).length === 0) {
      toast.error('Enter at least one field to save');
      return;
    }
    setSaving((s) => ({ ...s, zoho: true }));
    try {
      await axios.put(`${API}/admin/integrations/zoho`, payload, { headers: getAuthHeader() });
      toast.success('Zoho settings saved');
      setZohoForm({ client_id: '', client_secret: '', refresh_token: '', organization_id: zohoForm.organization_id, redirect_uri: zohoForm.redirect_uri, data_center: zohoForm.data_center });
      setTestResult((r) => ({ ...r, zoho: null }));
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save Zoho settings');
    } finally {
      setSaving((s) => ({ ...s, zoho: false }));
    }
  };

  const testZoho = async () => {
    setTesting((t) => ({ ...t, zoho: true }));
    setTestResult((r) => ({ ...r, zoho: null }));
    try {
      const res = await axios.post(`${API}/admin/integrations/zoho/test`, {}, { headers: getAuthHeader() });
      setTestResult((r) => ({ ...r, zoho: res.data }));
      if (res.data.success) toast.success(res.data.message);
      else toast.error(res.data.message);
    } catch (err) {
      setTestResult((r) => ({ ...r, zoho: { success: false, message: err.response?.data?.detail || 'Test failed' } }));
      toast.error('Test failed');
    } finally {
      setTesting((t) => ({ ...t, zoho: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-integrations-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <PlugZap className="w-7 h-7 text-amber-400" />
            Integrations
          </h1>
          <p className="text-slate-400 mt-1">Manage API credentials for payment and invoicing services</p>
        </div>
      </div>

      {/* Security Banner */}
      <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 flex gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-200">
          <p className="font-medium">Sensitive credentials</p>
          <p className="text-amber-300/70 mt-1">
            Saved secrets are stored in the database and override values in the server's <code className="bg-slate-900 px-1 py-0.5 rounded">.env</code> file. Leave fields blank to keep the currently-saved value. Always use your production keys only on the live environment.
          </p>
        </div>
      </div>

      {/* Razorpay Card */}
      <Card className="bg-slate-900/50 border-slate-800" data-testid="razorpay-card">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <CardTitle className="text-white">Razorpay</CardTitle>
                <CardDescription className="text-slate-400">
                  Payment gateway for Will purchases and partner onboarding fees
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {data.razorpay?.configured ? (
                <span className="px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-xs font-medium flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Configured
                </span>
              ) : (
                <span className="px-2 py-1 rounded-full bg-red-500/10 text-red-400 text-xs font-medium">Not configured</span>
              )}
              {data.razorpay?.mode && (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${data.razorpay.mode === 'live' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`}>
                  {data.razorpay.mode.toUpperCase()}
                </span>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300 mb-2 block">Key ID</Label>
              <SecretInput
                value={rzpForm.key_id}
                onChange={(v) => setRzpForm({ ...rzpForm, key_id: v })}
                placeholder="rzp_live_XXXXXXXXXXXX"
                currentMasked={data.razorpay?.key_id_masked}
                testId="razorpay-key-id-input"
              />
            </div>
            <div>
              <Label className="text-slate-300 mb-2 block">Key Secret</Label>
              <SecretInput
                value={rzpForm.key_secret}
                onChange={(v) => setRzpForm({ ...rzpForm, key_secret: v })}
                placeholder="Enter key secret"
                currentMasked={data.razorpay?.key_secret_masked}
                testId="razorpay-key-secret-input"
              />
            </div>
            <div>
              <Label className="text-slate-300 mb-2 block">Webhook Secret</Label>
              <SecretInput
                value={rzpForm.webhook_secret}
                onChange={(v) => setRzpForm({ ...rzpForm, webhook_secret: v })}
                placeholder="Enter webhook secret"
                currentMasked={data.razorpay?.webhook_secret_masked}
                testId="razorpay-webhook-secret-input"
              />
            </div>
            <div>
              <Label className="text-slate-300 mb-2 block">Mode</Label>
              <select
                value={rzpForm.mode}
                onChange={(e) => setRzpForm({ ...rzpForm, mode: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-md text-white px-3 py-2"
                data-testid="razorpay-mode-select"
              >
                <option value="">(unchanged)</option>
                <option value="test">Test</option>
                <option value="live">Live</option>
              </select>
            </div>
          </div>

          {testResult.razorpay && (
            <div className={`p-3 rounded-lg flex items-start gap-2 ${testResult.razorpay.success ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
              {testResult.razorpay.success ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
              ) : (
                <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
              )}
              <p className={`text-sm ${testResult.razorpay.success ? 'text-emerald-300' : 'text-red-300'}`}>
                {testResult.razorpay.message}
              </p>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              onClick={saveRazorpay}
              disabled={saving.razorpay}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
              data-testid="razorpay-save-btn"
            >
              {saving.razorpay ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Razorpay Settings
            </Button>
            <Button
              onClick={testRazorpay}
              disabled={testing.razorpay || !data.razorpay?.configured}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
              data-testid="razorpay-test-btn"
            >
              {testing.razorpay ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlugZap className="w-4 h-4 mr-2" />}
              Test Connection
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Zoho Card */}
      <Card className="bg-slate-900/50 border-slate-800" data-testid="zoho-card">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                <FileText className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <CardTitle className="text-white">Zoho Books</CardTitle>
                <CardDescription className="text-slate-400">
                  Invoice generation and accounting records
                </CardDescription>
              </div>
            </div>
            {data.zoho?.configured ? (
              <span className="px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-xs font-medium flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Configured
              </span>
            ) : (
              <span className="px-2 py-1 rounded-full bg-red-500/10 text-red-400 text-xs font-medium">Not configured</span>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300 mb-2 block">Client ID</Label>
              <SecretInput
                value={zohoForm.client_id}
                onChange={(v) => setZohoForm({ ...zohoForm, client_id: v })}
                placeholder="1000.XXXXXXXXXXXX"
                currentMasked={data.zoho?.client_id_masked}
                testId="zoho-client-id-input"
              />
            </div>
            <div>
              <Label className="text-slate-300 mb-2 block">Client Secret</Label>
              <SecretInput
                value={zohoForm.client_secret}
                onChange={(v) => setZohoForm({ ...zohoForm, client_secret: v })}
                placeholder="Enter client secret"
                currentMasked={data.zoho?.client_secret_masked}
                testId="zoho-client-secret-input"
              />
            </div>
            <div>
              <Label className="text-slate-300 mb-2 block">Refresh Token</Label>
              <SecretInput
                value={zohoForm.refresh_token}
                onChange={(v) => setZohoForm({ ...zohoForm, refresh_token: v })}
                placeholder="1000.XXXXXXXXXXXXXXXXXXXXXXXX"
                currentMasked={data.zoho?.refresh_token_masked}
                testId="zoho-refresh-token-input"
              />
            </div>
            <div>
              <Label className="text-slate-300 mb-2 block">Organization ID</Label>
              <Input
                value={zohoForm.organization_id}
                onChange={(e) => setZohoForm({ ...zohoForm, organization_id: e.target.value })}
                placeholder="e.g., 60000000000"
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="zoho-org-id-input"
              />
            </div>
            <div>
              <Label className="text-slate-300 mb-2 block">
                Data Center
                <span className="text-xs text-slate-500 ml-2">(Zoho region your account was created on)</span>
              </Label>
              <select
                value={zohoForm.data_center}
                onChange={(e) => setZohoForm({ ...zohoForm, data_center: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-md text-white px-3 py-2"
                data-testid="zoho-data-center-select"
              >
                <option value="in">India (.in)</option>
                <option value="com">United States (.com)</option>
                <option value="eu">Europe (.eu)</option>
                <option value="au">Australia (.com.au)</option>
                <option value="jp">Japan (.jp)</option>
                <option value="ca">Canada (.ca)</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-slate-300 mb-2 block">Redirect URI</Label>
              <Input
                value={zohoForm.redirect_uri}
                onChange={(e) => setZohoForm({ ...zohoForm, redirect_uri: e.target.value })}
                placeholder="https://yourdomain.com/api/zoho/callback"
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="zoho-redirect-uri-input"
              />
            </div>
          </div>

          {testResult.zoho && (
            <div className={`p-3 rounded-lg flex items-start gap-2 ${testResult.zoho.success ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
              {testResult.zoho.success ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
              ) : (
                <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <p className={`text-sm ${testResult.zoho.success ? 'text-emerald-300' : 'text-red-300'}`}>
                  {testResult.zoho.message}
                </p>
                {testResult.zoho.organizations && testResult.zoho.organizations.length > 0 && (
                  <ul className="mt-2 space-y-1 text-xs text-emerald-400/70">
                    {testResult.zoho.organizations.map((o) => (
                      <li key={o.id}>
                        {o.name} <span className="font-mono">({o.id})</span>
                      </li>
                    ))}
                  </ul>
                )}
                {testResult.zoho.per_dc_errors && testResult.zoho.per_dc_errors.length > 0 && (
                  <details className="mt-2">
                    <summary className="text-xs text-red-400/70 cursor-pointer hover:text-red-300">
                      Show per-data-center details
                    </summary>
                    <ul className="mt-2 space-y-1 text-xs text-red-400/70 font-mono">
                      {testResult.zoho.per_dc_errors.map((e, i) => (
                        <li key={i}>
                          <span className="font-semibold">{e.dc.toUpperCase()}:</span> {e.error}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              onClick={saveZoho}
              disabled={saving.zoho}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
              data-testid="zoho-save-btn"
            >
              {saving.zoho ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Zoho Settings
            </Button>
            <Button
              onClick={testZoho}
              disabled={testing.zoho || !data.zoho?.configured}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
              data-testid="zoho-test-btn"
            >
              {testing.zoho ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlugZap className="w-4 h-4 mr-2" />}
              Test Connection
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Integrations;
